import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { CheckIcon, ExternalLinkIcon, XIcon } from "lucide-react";
import { Button } from "@babackapp/ui/components/button";
import { Card } from "@babackapp/ui/components/card";
import { CardContent, CardHeader } from "@babackapp/ui/components/card";
import { generatePaymentMethodAdditionLinkQuery } from "@babackapp/private-api-client/client/use-generate-payment-method-addition-link-query";
import { useGetCarrierInformationPerMonthQuery } from "@babackapp/private-api-client/client/use-get-carrier-information-per-month-query";
import { useGetNumberOfReturnRequestPerMonthQuery } from "@babackapp/private-api-client/client/use-get-number-of-order-per-month";
import useTranslation from "@babackapp/translations/use-translation";
import Header from "@/components/layouts/header";
import { format, startOfMonth, getUnixTime } from "date-fns";
import { useGetCustomerPortalLink } from "@babackapp/private-api-client/client/use-get-customer-portal-link";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";

const BillingSettings = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const navigate = useNavigate();

  const [activatedPlan, setActivatedPlan] = useState<string[]>([]);
  const [unactivatedPlan, setUnactivatedPlan] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState({
    label: format(new Date(), "M"),
    key: getUnixTime(startOfMonth(new Date())) * 1000,
  });
  const currentPlanFromShop = shop?.plan;
  const getCustomerPortalLink = useGetCustomerPortalLink(shop.id);

  const [totalPriceTransport, setTotalPriceTransport] = useState(0);
  const [currency, setCurrency] = useState<"EUR" | "USD">("EUR");
  const [showTransportDetails, setShowTransportDetails] = useState(false);
  const isShopifyTestWebsite = shop.email.includes("shopify.com");
  if (isShopifyTestWebsite) {
    navigate(`/shops/${shop.id}`);
  }
  const addPaymentMethod = useCallback(async () => {
    const { url } = await generatePaymentMethodAdditionLinkQuery({
      shopId: shop.id,
    });
    window.open(url, "_blank");
  }, [shop]);

  const checkPlansAvailability = () => {
    const activated: string[] = [];
    const unactivated: string[] = [];
    const featureKey = [
      "customDomain",
      "chatSupport",
      "transportManagedByBB",
      "klaviyo",
      "zendesk",
      "workflowLogistician",
      "customisation",
      "api",
      "workflows",
    ];

    Object.entries(currentPlanFromShop || []).forEach(([key, value]) => {
      if (featureKey.includes(key)) {
        if (key === "transportManagedByBB" || key === "api") {
          if (currentPlanFromShop)
            currentPlanFromShop[key]?.activated
              ? activated.push(key)
              : unactivated.push(key);
        } else {
          value ? activated.push(key) : unactivated.push(key);
        }
      }
    });
    shop?.discount.available
      ? activated.push("discount")
      : unactivated.push("discount");
    shop?.exchange.available
      ? activated.push("exchange")
      : unactivated.push("exchange");

    setActivatedPlan(activated);
    setUnactivatedPlan(unactivated);
  };

  const calculateTotalPriceTransport = () => {
    let totalPrice = 0;
    let currentCurrency: "EUR" | "USD" = "EUR";

    carrierInformationPerMonth?.forEach((info: any) => {
      info.carrierValues.forEach((carrier: any) => {
        totalPrice += carrier.price * carrier.count;
        currentCurrency = carrier.currency;
      });
    });

    setCurrency(currentCurrency);
    setTotalPriceTransport(totalPrice);
  };

  useEffect(() => {
    checkPlansAvailability();
  }, [shop?.plan]);
  const {
    data: carrierInformationPerMonth,
    refetch: refetchCarrierInformationPerMonth,
  } = useGetCarrierInformationPerMonthQuery({
    shopId: shop?.id,
    startMonthTimeStamp: selectedMonth.key.toString(),
  });

  const { data: numberRequestsPerMonth } =
    useGetNumberOfReturnRequestPerMonthQuery({
      shopId: shop?.id,
      startMonthTimeStamp: (
        getUnixTime(startOfMonth(new Date())) * 1000
      ).toString(),
    });

  useEffect(() => {
    refetchCarrierInformationPerMonth();
  }, [selectedMonth]);

  useEffect(() => {
    calculateTotalPriceTransport();
    if (carrierInformationPerMonth?.length === 0) {
      setShowTransportDetails(false);
    }
  }, [carrierInformationPerMonth]);

  return (
    <div className="space-y-6 w-full">
      <Header
        title={t("settings.billing.billing")}
        subtitle={t("settings.billing.billingSubtitle")}
      />
      <div className="py-2 md:px-6 lg:px-8 space-y-8">
        {!shop?.billing.stripe?.paymentMethodId && (
          <div className="bg-white rounded-lg border-2 border-red-500 font-bold text-red-500">
            <div className="px-4 py-5 space-y-2 sm:p-6">
              <div className="space-y-6">
                <div className="flex space-x-2">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
                  <div className="flex-1">
                    <h3 className="text-lg font-normal text-gray-900">
                      {t("settings.billing.billingAlertTitle")}
                    </h3>
                    <p className="max-w-2xl text-sm font-light text-gray-900">
                      {t("settings.billing.billingAlertSubtitle")}
                    </p>
                    <Button
                      size="sm"
                      variant="link"
                      className="px-0"
                      onClick={() => addPaymentMethod()}
                    >
                      <ExternalLinkIcon className="size-4 mr-1" />
                      {t("settings.billing.updatePayementMethod")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Card>
          <CardHeader className="space-y-6 divide-y w-full border-b border-slate-200 bg-white">
            <div className="flex justify-between">
              <div>
                <p className="font-normal text-lg">
                  {t("settings.billing.currentPlan")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("settings.billing.currentPlanSubtitle")}
                </p>
              </div>
              <Button
                variant="default"
                onClick={() =>
                  window.open(
                    "mailto:support@baback.co?subject=Changement d'abonnement",
                  )
                }
              >
                {t("settings.billing.contactUs")}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                {activatedPlan.map((feature) => (
                  <LineItemPlanAvailable
                    key={feature}
                    featureName={t(`settings.billing.features.${feature}`)}
                  />
                ))}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 font-medium">
                  {t("settings.billing.notIncluded")}
                </p>
                {unactivatedPlan.map((feature) => (
                  <LineItemPlanUnavailable
                    key={feature}
                    featureName={t(`settings.billing.features.${feature}`)}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="space-y-6 divide-y w-full border-b border-slate-200 bg-white">
            <div className="space-y-6 ">
              <div className="flex">
                <div className="flex-1">
                  <div className="font-normal text-lg text-black">
                    {t("settings.billing.paymentAndBilling")}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    {t("settings.billing.paymentAndBillingSubtitle")}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="pt-6 flex items-center">
              <div className="font-medium w-fit text-sm text-black">
                {t("settings.billing.paymentMethod")}
              </div>
              <Button
                variant="link"
                onClick={() => addPaymentMethod()}
                className=""
              >
                {t("settings.billing.updatePayementMethod")}
              </Button>
            </div>
            {getCustomerPortalLink.data && (
              <div className="pt-6 flex items-center">
                <div className="font-medium w-fit text-sm text-black flex items-center">
                  <div>{t("settings.billing.myBillings")}</div>
                  <Button
                    variant={"link"}
                    onClick={() => {
                      const portalLink = getCustomerPortalLink.data;
                      if (portalLink) {
                        window.open(portalLink, "_blank");
                      }
                    }}
                  >
                    {t("settings.billing.seeBills")}
                    <ExternalLinkIcon className="size-4 mr-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const LineItemPlanAvailable = ({ featureName }: { featureName: string }) => (
  <li className="flex space-x-3">
    <CheckIcon className="h-5 w-5 text-green-500" />
    <div className="text-sm text-gray-500">{featureName}</div>
  </li>
);

const LineItemPlanUnavailable = ({ featureName }: { featureName: string }) => (
  <li className="flex space-x-3">
    <XIcon className="h-5 w-5 text-red-500" />
    <div className="text-sm text-gray-500">{featureName}</div>
  </li>
);

export default BillingSettings;
